import { Injectable, inject } from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {environment} from '@environments/environment';
import {InfluencerImportRowPage, InfluencerImportRowQuery} from '@app/modules/shared/models/influencer-import-row';

@Injectable({
  providedIn: 'root'
})
export class InfluencerService {
  private http = inject(HttpClient);

  private baseApiUrl = environment.apiUrl;
  private taskUrl = this.baseApiUrl + '/api/v1/task';
  private influencerUrl = this.baseApiUrl + '/api/v1/influencer';
  private influencerSegmentUrl = this.baseApiUrl + '/api/v1/segment';

  getAll(params: any = {}) {
    return this.http.get(`${this.influencerUrl}`, {params});
  }

  getById(id: number) {
    return this.http.get(`${this.influencerUrl}/${id}`);
  }

  getByCode(code: string, params: any = {}) {
    return this.http.get(`${this.influencerUrl}/update/${code}`, { params });
  }

  getSegments(params: any = {}) {
    return this.http.get(this.influencerSegmentUrl, {params, headers: new HttpHeaders().set('Use-Temp-Token', 'true')});
  }

  // TODO:- BACKEND API NOT AVAILABLE
  updateSegment(oldGroup, oldName, newName) {
    const segmentEdit = {
      oldGroup,
      oldName,
      newName
    };
    return this.http.post(`${this.baseApiUrl}/private/updatesegment`, segmentEdit);
  }

  // TODO:- BACKEND API NOT AVAILABLE
  // These four send plain JSON objects (the CSV was uploaded separately and travels as a
  // reference). They used to declare multipart/form-data anyway; the interceptor silently
  // rewrote that to JSON until it was taught to respect an existing Content-Type, at which
  // point the server received a body it could not parse and every import stopped.
  validateInfluencers(params) {
    return this.http.post(`${this.influencerUrl}/import`, params);
  }

  csvToValuesInfluencers(params) {
    return this.http.post(`${this.influencerUrl}/csv_to_values`, params);
  }

  importHeadersInfluencers(params) {
    return this.http.post(`${this.influencerUrl}/importHeaders`, params);
  }

  importInfluencers(params) {
    return this.http.post(`${this.influencerUrl}/import`, params);
  }

  deleteInfluencer(id: number) {
    return this.http.delete(`${this.influencerUrl}/${id}`);
  }

  deleteCampaignInfluencerAppliedAction(id: number, campaignId: number) {
    return this.http.delete(`${this.influencerUrl}/${id}/campaign/${campaignId}/applied_action`);
  }

  getCampaignAppliedActions(campaignId: number) {
    return this.http.get(`${this.baseApiUrl}/api/v1/campaign_influencer/applied_actions`, {
      params: { campaign_id: String(campaignId) }
    });
  }

  importInfluencersStatus(influencerJobId: number) {
    return this.http.get(`${this.influencerUrl}/import/status/${influencerJobId}`);
  }

  /** One page of an import's row log (its task log), with the run's counts per outcome. */
  importRows(projectId: number, taskLogId: number, query: InfluencerImportRowQuery = {}): Observable<InfluencerImportRowPage> {
    const params: Record<string, string> = {};
    if (query.outcome) {
      params.outcome = query.outcome;
    }
    if (query.search?.trim()) {
      params.search = query.search.trim();
    }
    if (query.page) {
      params.page = String(query.page);
    }
    if (query.perPage) {
      params.perPage = String(query.perPage);
    }
    return this.http.get<{data: InfluencerImportRowPage}>(
      `${this.baseApiUrl}/api/v1/project/${projectId}/influencer-import/${taskLogId}/rows`, {params})
      .pipe(map((response) => response.data));
  }

  bulkInfluencersStatus(influencerJobId: number) {
    return this.http.get(`${this.influencerUrl}/bulk/status/${influencerJobId}`);
  }

  exportInfluencers(params: any = {}) {
    // return this.http.get(`${this.influencerUrl}/export`, {params, headers: new HttpHeaders().set('Use-Temp-Token', 'true')});
    return this.http.post(`${this.influencerUrl}/export`, params);
  }

  addInfluencer(influencer) {
    return this.http.post(`${this.influencerUrl}`, influencer);
  }

  updateInfluencer(influencer, influencerId: number) {
    return this.http.put(`${this.influencerUrl}/${influencerId}`, influencer);
  }

  updateInfluencerByCode(influencer, code: string) {
    return this.http.put(`${this.influencerUrl}/update/${code}`, influencer);
  }

  getInfluencerType(params: any) {
    return this.http.get(`${this.influencerUrl}/type`, {params, headers: new HttpHeaders().set('Use-Temp-Token', 'true')});
  }

  getInfluencerReferralsList(influencerId: number, params?: any) {
    return this.http.get(`${this.influencerUrl}/${influencerId}/referral_list`, {
      params
    });
  }

  taskInfluencersLogs(params: any) {
    return this.http.get(`${this.taskUrl}/logs`, {params});
  }

  bulkInfluencerOperations(params: any) {
    return this.http.post(`${this.influencerUrl}s/bulk`, params);
  }



  importInfluencersFields(influencerTypeId: number) {
    console.log('--importInfluencersFields: ', influencerTypeId);
    return this.http.get(`${this.influencerUrl}/import/template_fields/${influencerTypeId}`);
  }

  // uploadInfluencerImage(image) {
  //   return new Promise(async (res, rej) => {
  //     const isInstanceOfFile = image instanceof File;
  //
  //     if (!isInstanceOfFile) {
  //       res(image);
  //       return;
  //     }
  //
  //     const upload = await this.awsService.uploadFile(image);
  //     if (!upload) {
  //       rej('An error occurred while uploading the influencer image');
  //       return;
  //     }
  //
  //     const {origin, pathname} = new URL(upload['url']);
  //
  //     res(`${origin}${pathname}`);
  //   });
  // }

  // uploadTeamImages(team, atrribute) {
  //   if (!team || !team.length) {
  //     return team;
  //   }
  //
  //   return new Promise(async (res) => {
  //     const promises = team.map((member) => {
  //       return new Promise(async (resolve) => {
  //         if (!member[atrribute]) {
  //           resolve(member);
  //         }
  //
  //         member[atrribute] = await this.uploadInfluencerImage(member[atrribute]);
  //         resolve(member);
  //       });
  //     });
  //     res(await Promise.all(promises));
  //   });
  // }
}
